import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_ORGANIZATION_BY_SLUG } from "../../division/queries/GetDivisions";
import {useBranding} from "../../../context/BrandingContext";

const AUTH_KEY = "auth";

function safeParseAuth() {
    if (typeof window === "undefined") return null;

    try {
        const raw = window.localStorage.getItem(AUTH_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function normalizeSlug(value) {
    const normalized = String(value ?? "").trim();
    return normalized || null;
}

function getActiveProfile(profiles = []) {
    return profiles.find((p) => p?.activeInWeb === true) || profiles[0] || null;
}

export default function useActiveOrganization(orgSlugOverride, options = {}) {
    const params = useParams();
    const orgSlug = normalizeSlug(orgSlugOverride ?? params.orgSlug);

    const [authData, setAuthData] = useState(() => safeParseAuth());

    const refreshAuth = useCallback(() => {
        setAuthData(safeParseAuth());
    }, []);

    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === AUTH_KEY) refreshAuth();
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener("kbola-auth-updated", refreshAuth);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("kbola-auth-updated", refreshAuth);
        };
    }, [refreshAuth]);

    // ✅ Memoize arrays so deps don't change every render
    const profiles = useMemo(() => authData?.user?.profiles ?? [], [authData]);

    const activeProfile = useMemo(() => getActiveProfile(profiles), [profiles]);

    const profileOrganizations = useMemo(
        () => activeProfile?.profileOrganizations ?? [],
        [activeProfile]
    );

    const activeProfileOrganization = useMemo(() => {
        if (!orgSlug) return null;

        const slugLower = String(orgSlug).toLowerCase();

        return (
            profileOrganizations.find((po) => {
                const s = po?.organization?.slug;
                return s && String(s).toLowerCase() === slugLower;
            }) || null
        );
    }, [profileOrganizations, orgSlug]);

    const {organization: brandedOrganization} = useBranding();
    const {
        data: organizationData,
        loading: organizationLoading,
        error: organizationError,
        refetch,
    } = useQuery(GET_ORGANIZATION_BY_SLUG, {
        variables: {organizationSlug: orgSlug},
        skip: options.skipQuery || !orgSlug,
        fetchPolicy: "cache-and-network",
    });

    const queriedOrganization = organizationData?.organization ?? null;
    const queriedOrganizationMatchesSlug = Boolean(
        orgSlug && queriedOrganization?.slug?.toLowerCase() === orgSlug.toLowerCase()
    );
    const brandedOrganizationMatchesSlug = brandedOrganization?.slug?.toLowerCase() === orgSlug?.toLowerCase();
    const organization = queriedOrganizationMatchesSlug
        ? {...(activeProfileOrganization?.organization || {}), ...queriedOrganization}
        : activeProfileOrganization?.organization
            ?? (!orgSlug || brandedOrganizationMatchesSlug ? brandedOrganization : null);
    const activeSubscription = organization?.activeSubscription ?? null;
    const hasAccess = Boolean(activeProfileOrganization);
    const loading = Boolean(orgSlug && organizationLoading && !activeProfileOrganization);
    const ready = Boolean(authData) && (!orgSlug || !organizationLoading);
    const status = !orgSlug
        ? "missing-slug"
        : loading
            ? "loading"
            : organizationError && !organization
                ? "error"
                : !organization
                    ? "not-found"
                    : !hasAccess
                        ? "no-access"
                        : "ready";

    return {
        orgSlug,

        authData,
        refreshAuth,

        profiles,
        activeProfile,

        activeProfileOrganization,
        organization,
        activeSubscription,

        found: Boolean(organization),
        hasAccess,
        hasOrgSlug: Boolean(orgSlug),
        ready,
        loading,
        error: organizationError ?? null,
        status,
        refetch,
    };
}
